import React, { PropsWithChildren, useCallback, useContext, useRef } from 'react';

import { CustomBottomSheetModalProvider } from '@/feature/alerts/components/CustomBottomSheetModalProvider';
import {
    PickTypesEducationModal,
    PickTypesEducationModalRef,
} from '@/feature/educational-picks/components/PickTypesEducationModal';
import { PickTypesEducationTarget } from '@/feature/educational-picks/utils/pick-types-education-target';

type PickTypesEducation = {
    /** Opens the modal, pre-selecting the tab matching `target` (falls back to the first tab). */
    showPickTypesEducationModal: (target?: PickTypesEducationTarget) => void;
};

const PickTypesEducationContext = React.createContext<PickTypesEducation | undefined>(undefined);

export const PickTypesEducationProvider: React.FC<PropsWithChildren> = ({ children }) => {
    const modalRef = useRef<PickTypesEducationModalRef>(null);

    const showPickTypesEducationModal = useCallback((target?: PickTypesEducationTarget) => {
        modalRef.current?.show(target);
    }, []);

    return (
        <CustomBottomSheetModalProvider>
            <PickTypesEducationContext.Provider value={{ showPickTypesEducationModal }}>
                {children}
                <PickTypesEducationModal ref={modalRef} />
            </PickTypesEducationContext.Provider>
        </CustomBottomSheetModalProvider>
    );
};

export const usePickTypesEducation = () => {
    const context = useContext(PickTypesEducationContext);
    if (context === undefined) {
        throw new Error('usePickTypesEducation must be used within a PickTypesEducationProvider');
    }
    return context;
};
